import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	AttributeVocabularyField,
	type IAttributeVocabulary,
} from './attributeVocabularySchema';

export const ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS = [
	AttributeVocabularyField.name,
	AttributeVocabularyField.modDate,
	AttributeVocabularyField.count,
] as const;

export const AttributeVocabularySortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS),
});

export type AttributeVocabularySort = z.infer<
	typeof AttributeVocabularySortSchema
>;

export const AttributeVocabularySortableField = z.enum(
	ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS,
).enum;

export const ATTRIBUTE_VOCABULARY_DEFAULT_SORT_FIELD =
	AttributeVocabularySortableField.name;

export const ATTRIBUTE_VOCABULARY_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getAttributeVocabularyDefaultSort = (
	sort: Partial<AttributeVocabularySort> = {},
): AttributeVocabularySort => ({
	field: getAttributeVocabularySortField({sortField: sort.field}),
	order: getAttributeVocabularySortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getAttributeVocabularySortField = (
	params: Omit<
		GetSortFieldParams<(typeof ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS,
		defaultSortField: ATTRIBUTE_VOCABULARY_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getAttributeVocabularySortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: ATTRIBUTE_VOCABULARY_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const editAttributeVocabularyEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IAttributeVocabulary,
			(typeof ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: ATTRIBUTE_VOCABULARY_SORTABLE_FIELDS,
	});
